import { useState } from "react";
import { Loader, MiniTile, useReport, ExportCsvButton } from "../shared";
import { Badge } from "@/components/ui/badge";
import { Truck, CaretDown, CaretUp, Trophy } from "@phosphor-icons/react";

const BRAND_LABEL = { doordash: "DoorDash", ubereats: "Uber Eats", grubhub: "Grubhub" };
const BRAND_COLOR = {
  doordash: "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200",
  ubereats: "bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100",
  grubhub: "bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-200",
};

const fmtCents = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);
const fmtEta = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
};
const fmtEtaFromTs = (ts) => {
  if (!ts) return "—";
  try {
    return new Date(ts * 1000).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch { return "—"; }
};

const DispatchRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  const losers = (row.competing_quotes || []).filter((q) => q.brand !== row.winner);
  const brandKey = row.winner || "unknown";
  return (
    <>
      <tr className="border-b hover:bg-muted/30" data-testid={`dispatch-row-${row.order_id}`}>
        <td className="px-3 py-2 text-xs font-mono text-muted-foreground">
          {(row.closed_at || "").replace("T", " ").slice(0, 16)}
        </td>
        <td className="px-3 py-2 text-sm font-mono">#{row.order_number || row.order_id?.slice(0, 6)}</td>
        <td className="px-3 py-2 text-sm">{row.customer_name || "—"}</td>
        <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[240px]">{row.delivery_address || "—"}</td>
        <td className="px-3 py-2">
          <Badge className={`text-[10px] ${BRAND_COLOR[brandKey] || ""}`}>
            <Trophy size={10} weight="fill" className="mr-1" />
            {BRAND_LABEL[brandKey] || brandKey}
          </Badge>
        </td>
        <td className="px-3 py-2 text-sm font-mono text-right">{fmtCents(row.winner_fee_cents)}</td>
        <td className="px-3 py-2 text-sm font-mono text-right">{fmtEta(row.winner_dropoff_estimated)}</td>
        <td className="px-3 py-2 text-right">
          {losers.length > 0 ? (
            <button
              onClick={() => setOpen(!open)}
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
              data-testid={`dispatch-row-${row.order_id}-toggle`}
            >
              {losers.length} competing
              {open ? <CaretUp size={12} /> : <CaretDown size={12} />}
            </button>
          ) : (
            <span className="text-xs text-muted-foreground">no rivals</span>
          )}
        </td>
      </tr>
      {open && losers.length > 0 && (
        <tr className="bg-muted/20">
          <td colSpan={8} className="px-3 py-2">
            <div className="text-xs text-muted-foreground mb-1.5">Losing quotes for this order:</div>
            <div className="flex flex-wrap gap-2">
              {losers.map((q, i) => (
                <div key={i} className="text-xs rounded-md border bg-background px-2 py-1 flex items-center gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${BRAND_COLOR[q.brand]?.split(" ")[0] || "bg-muted"}`} />
                  <span className="font-medium">{BRAND_LABEL[q.brand] || q.brand}</span>
                  <span className="font-mono text-muted-foreground">ETA {fmtEtaFromTs(q.dropoff_ts)}</span>
                  <span className="font-mono text-muted-foreground">{fmtCents(q.fee)}</span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export const DeliveryDispatchesSection = ({ filter }) => {
  const [data, loading] = useReport("/reports/delivery-dispatches", filter, false);
  if (loading) return <Loader />;
  if (!data) return null;

  const rangeLabel = `${data.start_date} → ${data.end_date}`;
  const totalDispatches = data.rows.length;
  const totalSpend = data.rows.reduce((sum, r) => sum + (r.winner_fee_cents || 0), 0);

  return (
    <div className="space-y-4" data-testid="dispatches-section">
      <div className="flex justify-end">
        <ExportCsvButton
          path="/reports/delivery-dispatches/export.csv"
          filter={filter}
          isSingleDay={false}
          testId="export-dispatches-csv"
        />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniTile label="Auto-Dispatches" value={totalDispatches} testId="dispatches-count" />
        <MiniTile label="Total Fees Paid" value={fmtCents(totalSpend)} tone="text-emerald-700" testId="dispatches-spend" />
        <MiniTile
          label="Avg Fee / Delivery"
          value={fmtCents(totalDispatches ? Math.round(totalSpend / totalDispatches) : 0)}
          testId="dispatches-avg"
        />
        <MiniTile label="Window" value={rangeLabel} valueClass="text-sm" testId="dispatches-window" />
      </div>

      {/* Brand summary */}
      <div className="rounded-lg border border-border overflow-hidden" data-testid="dispatches-brand-summary">
        <div className="px-3 py-2 bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-2">
          <Truck size={14} weight="fill" /> Brand Win Summary
        </div>
        <div className="divide-y divide-border">
          {data.brand_summary.length === 0 && (
            <div className="px-3 py-3 text-sm text-muted-foreground">No third-party dispatches yet in this window.</div>
          )}
          {data.brand_summary.map((b) => (
            <div key={b.brand} className="px-3 py-2 flex items-center justify-between text-sm" data-testid={`brand-summary-${b.brand}`}>
              <div className="flex items-center gap-2">
                <Badge className={`text-[10px] ${BRAND_COLOR[b.brand] || ""}`}>{BRAND_LABEL[b.brand] || b.brand}</Badge>
                <span className="text-muted-foreground">{b.wins} order{b.wins === 1 ? "" : "s"}</span>
              </div>
              <div className="font-mono text-xs text-muted-foreground">
                avg <span className="text-foreground">{fmtCents(b.avg_fee_cents)}</span> · total <span className="text-foreground">{fmtCents(b.total_fee_cents)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Per-order table */}
      <div className="rounded-lg border border-border overflow-x-auto" data-testid="dispatches-table">
        {data.rows.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">
            No auto-dispatched delivery orders in this window.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">Closed</th>
                <th className="px-3 py-2 text-left">Order</th>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-left">Address</th>
                <th className="px-3 py-2 text-left">Winner</th>
                <th className="px-3 py-2 text-right">Fee</th>
                <th className="px-3 py-2 text-right">ETA</th>
                <th className="px-3 py-2 text-right">Competing</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <DispatchRow key={r.order_id} row={r} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DeliveryDispatchesSection;
